import { works } from "@/mocks/works";
import { categories } from "@/mocks/categories";
import type { Category, Work } from "@/types/gallery";

export function formatCount(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return `${value}`;
}

export function getWorkById(id: string): Work | undefined {
  return works.find((item) => item.id === id);
}

export function getCategoryById(id: string): Category | undefined {
  return categories.find((item) => item.id === id);
}

export function getWorksByCategory(categoryId: string): Work[] {
  return works.filter((item) => item.categoryId === categoryId);
}

export interface WorkFilters {
  styles: string[];
  scenes: string[];
  models: string[];
  years: string[];
}

export const emptyFilters: WorkFilters = {
  styles: [],
  scenes: [],
  models: [],
  years: [],
};

export function filterWorks(list: Work[], filters: WorkFilters): Work[] {
  return list.filter((work) => {
    if (filters.styles.length && !filters.styles.includes(work.style)) return false;
    if (filters.scenes.length && !filters.scenes.includes(work.scene)) return false;
    if (filters.models.length && !filters.models.includes(work.model)) return false;
    if (filters.years.length && !filters.years.includes(work.year)) return false;
    return true;
  });
}

export function getFilterOptions(list: Work[]) {
  return {
    styles: Array.from(new Set(list.map((item) => item.style))),
    scenes: Array.from(new Set(list.map((item) => item.scene))),
    models: Array.from(new Set(list.map((item) => item.model))),
    years: Array.from(new Set(list.map((item) => item.year))).sort((a, b) => b.localeCompare(a)),
  };
}

export function searchWorks(query: string): Work[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return works.filter((work) => {
    const haystack = [
      work.title,
      work.model,
      work.photographer,
      work.description,
      work.style,
      work.scene,
      work.tags.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
}

export function getRelatedWorks(current: Work, count = 6): Work[] {
  const sameCategory = works.filter(
    (item) => item.categoryId === current.categoryId && item.id !== current.id
  );
  const shuffled = [...sameCategory].sort(() => Math.random() - 0.5);
  if (shuffled.length >= 4) return shuffled.slice(0, count);
  const extra = works
    .filter((item) => item.id !== current.id && item.categoryId !== current.categoryId)
    .sort(() => Math.random() - 0.5);
  return [...shuffled, ...extra].slice(0, count);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    totalPages,
    total: list.length,
    items: list.slice(start, start + pageSize),
  };
}

export function getHotTags(): string[] {
  return ["白露", "时尚大片", "户外旅拍", "复古胶片", "棚拍艺术", "人像写真", "黑白", "海边"];
}